import type { App } from 'vue'
import PrimeVue from 'primevue/config'
import Aura from '@primeuix/themes/aura'
import Tooltip from 'primevue/tooltip'
import { definePreset } from '@primeuix/themes'
import ToastService from 'primevue/toastservice'
import createPromisify from '@/utils/createPromisify'
import { getCurrentLocale } from '@/utils/localeUtils'
import ConfirmationService from 'primevue/confirmationservice'

const langImport = {
  'en-US': () => import('primelocale/en.json').then((module) => module.default['en']),
  'zh-CN': () => import('primelocale/zh-CN.json').then((module) => module.default['zh-CN']),
}

const themePreset = definePreset(Aura, {
  semantic: {
    primary: {
      50: '{sky.50}',
      100: '{sky.100}',
      200: '{sky.200}',
      300: '{sky.300}',
      400: '{sky.400}',
      500: '{sky.500}',
      600: '{sky.600}',
      700: '{sky.700}',
      800: '{sky.800}',
      900: '{sky.900}',
      950: '{sky.950}',
    },
    colorScheme: {
      light: {
        surface: {
          0: '#ffffff',
          50: '#f7f9fc',
          100: '#eef3f8',
          200: '#d9e2ec',
          300: '{slate.300}',
          400: '{slate.400}',
          500: '{slate.500}',
          600: '{slate.600}',
          700: '{slate.700}',
          800: '{slate.800}',
          900: '{slate.900}',
          950: '{slate.950}',
        },
      },
    },
  },

  components: {
    // Keep shared tables on the light Aura surface hierarchy without changing
    // row status colors or the component's spacing and interaction behavior.
    datatable: {
      headerCell: {
        background: '{surface.50}',
        color: '{surface.600}',
      },
      row: {
        hoverBackground: '{surface.50}',
        hoverColor: '{surface.800}',
      },
    },
  },
})

const appUsePrimeVue = (app: App) => {
  const promisify = createPromisify()
  const locale = getCurrentLocale()

  langImport[locale]().then((locale) => {
    app.use(ToastService)
    app.use(ConfirmationService)
    app.directive('tooltip', Tooltip)

    app.use(PrimeVue, {
      locale,

      ripple: true,

      theme: {
        preset: themePreset,

        options: {
          darkModeSelector: false,

          cssLayer: {
            name: 'primevue',
            order: 'theme, base, primevue',
          },
        },
      },
    })

    promisify.resolve()
  })

  return promisify.instance
}

export default appUsePrimeVue
